import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
// mantine
import { useDisclosure } from '@mantine/hooks';
// icons
import { Settings } from 'lucide-react';
// components
import Logo from '@/components/Logo';
import SettingsDrawer from '@/modules/SettingsDrawer';
import NavLinkItem from '@/components/Layout/components/Navbar/components/NavLinkItem';
// utils
import navItems from '@/components/Layout/components/Navbar/Navbar.data';
import { useIsVibeBuilderEnabled } from '@/hooks/useIsVibeBuilderEnabled';
import { useMyRole } from '@/hooks/useMyRole';
// assets
import styles from '@/components/Layout/components/Navbar/Navbar.module.css';

const HIDDEN_FOR_TECHNICAL_SUPPORT = new Set([
    'conversations',
    'personas',
    'sessions'
]);

const Navbar = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const isVibeBuilderEnabled = useIsVibeBuilderEnabled();
    const { isTechnicalSupport, isLoading } = useMyRole();

    const visibleNavItems = useMemo(
        () =>
            navItems.filter((item) => {
                if (item.idn === 'vibeBuilder' && !isVibeBuilderEnabled) {
                    return false;
                }
                if (
                    !isLoading &&
                    isTechnicalSupport &&
                    item.idn &&
                    HIDDEN_FOR_TECHNICAL_SUPPORT.has(item.idn)
                ) {
                    return false;
                }
                return true;
            }),
        [isVibeBuilderEnabled, isTechnicalSupport, isLoading]
    );

    const [isDataTransferOpened, handler] = useDisclosure(false);

    useEffect(() => {
        if (searchParams.get('dataTransfer')) {
            handler.open();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isDataTransferOpened) {
            setSearchParams({ dataTransfer: 'true' });
        } else {
            searchParams.delete('dataTransfer');
            setSearchParams(searchParams);
        }
    }, [isDataTransferOpened, setSearchParams, searchParams]);

    const handleDrawerOpen = (event: React.MouseEvent) => {
        event.preventDefault();
        handler.open();
    };

    const handleDrawerClose = () => {
        handler.close();
    };

    return (
        <>
            <nav className={styles.nav}>
                <Logo className={styles.logo} variant="icon" />
                {visibleNavItems.map(({ icon, label, path }) => (
                    <NavLinkItem
                        icon={icon}
                        key={path}
                        label={label}
                        path={path}
                    />
                ))}
                <NavLinkItem
                    className={styles.dataTransfer}
                    icon={<Settings />}
                    label="Settings"
                    onClick={handleDrawerOpen}
                />
            </nav>
            <SettingsDrawer
                isOpened={isDataTransferOpened}
                onClose={handleDrawerClose}
            />
        </>
    );
};

export default Navbar;
